import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatFormFieldModule,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { SearchService } from './search.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { combineLatest, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ToIconColorPipe } from 'src/app/shared/pipes/nav-item.pipe';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-search-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    ToIconColorPipe,
    RouterLink,
    RouterLink,
    MatButtonModule,
    MatOptionModule,
    MatSelectModule,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
  ],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.sass'],
})
export class SearchComponent implements OnInit, OnDestroy {
  private readonly service = inject(SearchService);
  typeControl = new FormControl<string>('');
  skeletonArray = Array(1).fill(0);

  formControl = this.service.formControl;
  showOptions$ = this.service.showOptions$;
  private _isLoading = false;

  get isLoading(): boolean {
    return this._isLoading;
  }

  searchOptions$ = combineLatest([
    this.service.searchOptions$,
    this.typeControl.valueChanges.pipe(startWith('')),
    this.formControl.valueChanges.pipe(startWith(this.formControl.value || ''))
  ]).pipe(
    map(([options, selectedType, searchValue]) => {
      let activeType = selectedType;

      if (typeof searchValue === 'string' && searchValue.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(searchValue);
          if (parsed && parsed.type) {
            activeType = parsed.type;
          }
        } catch (e) {
        }
      }

      return options
        .map((o) => {
          if (activeType && o.type.toLowerCase() !== activeType.toLowerCase()) {
            return { ...o, data: [] };
          }

          return {
            ...o,
            data: o.data.slice(0, 3),
          };
        })
        .filter((o) => o.data.length > 0);
    })
  );

  private destroy$ = new Subject<void>();

  constructor(private eRef: ElementRef) {}

  ngOnInit(): void {
    this.service
      .search$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {});
    this.service.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => (this._isLoading = isLoading));
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const clickedInside = this.eRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.service.showAutocomplete(false);
    }
  }

  openAutocomplete(): void {
    this.service.showAutocomplete(true);
  }

  closeAutocomplete(): void {
    this.service.showAutocomplete(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearSearch(): void {
    this.formControl.patchValue('');
  }
  
  onTypeChange(): void {
    this.service.setSearchType(this.typeControl.value ?? '');
  }
}
